import { useEffect, useState, useMemo, useRef } from 'react';
import { Stage, Layer, Rect, Circle, Path, Image as KonvaImage, Group } from 'react-konva';
import Konva from 'konva';
import { env } from '@/app/config/environment';
import { formatCurrencyForDisplay } from '@/lib/utils/currencyFormat';
import {
  CANVAS_WIDTH,
  CANVAS_HEIGHT,
  POLYGON_CORNER_RADIUS,
  toPixel,
  computeImageFit,
  getRoundedPolygonPath,
} from '@/features/booking/types/floorPlanTypes';
import type { FloorPlanResponse, FloorPlanItemResponse } from '@/features/booking/types/floorPlanTypes';

interface FloorPlanViewerProps {
  floorPlan: FloorPlanResponse;
  availableTables: string[];
  selectedTable: string | null;
  onTableSelect: (tableNumber: string) => void;
  containerWidth: number;
  pricePerGuest?: number;
  currency?: string;
  inCartTables?: string[];
  localTables?: string[];
  soldOutCartTables?: string[];
}

// Color helpers — shimmer multiplier is applied to available items
const getTableFill = (
  tableNumber: string,
  availableTables: string[],
  shimmer: number,
  inCartTables: string[],
  localTables: string[],
  soldOutCartTables: string[] = []
): string => {
  if (soldOutCartTables.includes(tableNumber)) {
    return 'rgba(239, 68, 68, 0.4)'; // Darker red for sold-out-in-cart
  }
  if (inCartTables.includes(tableNumber)) {
    return 'rgba(59, 130, 246, 0.5)'; // Blue
  }
  if (localTables.includes(tableNumber)) {
    return 'rgba(234, 179, 8, 0.5)'; // Yellow
  }
  if (availableTables.includes(tableNumber)) {
    const alpha = 0.12 + 0.18 * shimmer; // pulses between 0.12 and 0.30
    return `rgba(34, 197, 94, ${alpha.toFixed(3)})`;
  }
  return 'rgba(239, 68, 68, 0.2)'; // Red
};

const getTableStroke = (
  tableNumber: string,
  availableTables: string[],
  shimmer: number,
  inCartTables: string[],
  localTables: string[],
  soldOutCartTables: string[] = []
): string => {
  if (soldOutCartTables.includes(tableNumber)) {
    return 'rgba(239, 68, 68, 0.9)'; // Brighter red border for sold-out-in-cart
  }
  if (inCartTables.includes(tableNumber)) {
    return 'rgba(59, 130, 246, 0.9)'; // Blue
  }
  if (localTables.includes(tableNumber)) {
    return 'rgba(234, 179, 8, 0.9)'; // Yellow
  }
  if (availableTables.includes(tableNumber)) {
    const alpha = 0.35 + 0.35 * shimmer; // pulses between 0.35 and 0.70
    return `rgba(34, 197, 94, ${alpha.toFixed(3)})`;
  }
  return 'rgba(239, 68, 68, 0.5)'; // Red
};

// Allow native touch scrolling globally for this session on map components
(Konva as any).preventDefault = false;

const FloorPlanViewer: React.FC<FloorPlanViewerProps> = ({
  floorPlan,
  availableTables,
  selectedTable,
  onTableSelect,
  containerWidth,
  pricePerGuest,
  currency,
  inCartTables = [],
  localTables = [],
  soldOutCartTables = [],
}) => {
  const [backgroundImage, setBackgroundImage] = useState<HTMLImageElement | null>(null);
  const [tooltip, setTooltip] = useState<{ visible: boolean; x: number; y: number; bottomY: number; item: FloorPlanItemResponse } | null>(null);
  const [shimmerValue, setShimmerValue] = useState(0);
  const stageRef = useRef<any>(null);
  const animFrameRef = useRef<number>(0);

  // Shimmer animation — smooth sine-wave pulse (period ≈ 2s)
  useEffect(() => {
    let startTime: number | null = null;
    const animate = (time: number) => {
      if (startTime === null) startTime = time;
      const elapsed = time - startTime;
      // sine wave: 0 → 1 → 0 over ~2 seconds
      const value = (Math.sin((elapsed / 1000) * Math.PI) + 1) / 2;
      setShimmerValue(value);
      animFrameRef.current = requestAnimationFrame(animate);
    };
    animFrameRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animFrameRef.current);
  }, []);

  // Load background image
  useEffect(() => {
    if (!floorPlan.image_id) return;

    const img = new window.Image();
    const imageUrl = `${env.MAIN_GCS_URL}/${floorPlan.image_id}`;
    img.src = imageUrl;
    img.onload = () => setBackgroundImage(img);
    img.onerror = () => {
      console.error('Failed to load floor plan background image:', imageUrl);
      setBackgroundImage(null);
    };
  }, [floorPlan.image_id]);

  // Calculate scale and display height
  const scale = containerWidth / CANVAS_WIDTH;
  const displayHeight = containerWidth / (CANVAS_WIDTH / CANVAS_HEIGHT); // 3:2 ratio

  // Compute image fit
  const imageFit = useMemo(() => {
    if (!backgroundImage) return null;
    return computeImageFit(backgroundImage.naturalWidth, backgroundImage.naturalHeight);
  }, [backgroundImage]);

  const renderTableItem = (item: FloorPlanItemResponse) => {
    const px = toPixel(item.x, CANVAS_WIDTH);
    const py = toPixel(item.y, CANVAS_HEIGHT);
    const fill = getTableFill(item.table_number, availableTables, shimmerValue, inCartTables, localTables, soldOutCartTables);
    const stroke = getTableStroke(item.table_number, availableTables, shimmerValue, inCartTables, localTables, soldOutCartTables);
    const strokeWidth = selectedTable === item.table_number ? 3 : 2;
    const isSoldOutInCart = soldOutCartTables.includes(item.table_number);
    const isClickable = availableTables.includes(item.table_number) && !isSoldOutInCart;

    const handleClick = () => {
      if (isClickable) {
        onTableSelect(item.table_number);
      }
    };

    switch (item.shape) {
      case 'Rectangle': {
        const w = toPixel(item.width || 0, CANVAS_WIDTH);
        const h = toPixel(item.height || 0, CANVAS_HEIGHT);

        return (
          <Group
            key={item.id}
            x={px}
            y={py}
            rotation={item.rotation || 0}
            onClick={handleClick}
            onTap={handleClick}
            onMouseEnter={(e) => {
              const container = e.target.getStage()?.container();
              if (container) container.style.cursor = 'pointer';
              
              // We can use the mouse position from the event directly for the tooltip
              // or calculate center of shape. let's use mouse position for now or shape center.
              // Konva event has evt which is the native event
              
              const shape = e.target;
              const box = shape.getClientRect();
              
              // Calculate position relative to the container
              setTooltip({
                 visible: true,
                 x: box.x + box.width / 2,
                 y: box.y,
                 bottomY: box.y + box.height,
                 item,
              });
            }}
            onMouseLeave={(e) => {
               const container = e.target.getStage()?.container();
               if (container) container.style.cursor = 'default';
               setTooltip(null);
            }}
          >
            <Rect
              x={-w / 2}
              y={-h / 2}
              width={w}
              height={h}
              fill={fill}
              stroke={stroke}
              strokeWidth={strokeWidth}
              cornerRadius={4}
            />
          </Group>
        );
      }

      case 'Circle': {
        const r = toPixel(item.radius || 0, CANVAS_WIDTH);

        return (
          <Group
            key={item.id}
            x={px}
            y={py}
            onClick={handleClick}
            onTap={handleClick}
            onMouseEnter={(e) => {
              const container = e.target.getStage()?.container();
              if (container) container.style.cursor = 'pointer';
              
              const shape = e.target;
              const box = shape.getClientRect();
              
              setTooltip({
                 visible: true,
                 x: box.x + box.width / 2,
                 y: box.y,
                 bottomY: box.y + box.height,
                 item,
              });
            }}
            onMouseLeave={(e) => {
               const container = e.target.getStage()?.container();
               if (container) container.style.cursor = 'default';
               setTooltip(null);
            }}
          >
            <Circle
              x={0}
              y={0}
              radius={r}
              fill={fill}
              stroke={stroke}
              strokeWidth={strokeWidth}
            />
          </Group>
        );
      }

      case 'Polygon': {
        if (!item.points || item.points.length === 0) return null;

        // Points are RELATIVE to center, also normalized
        const flatPoints = item.points.flatMap((p) => [
          toPixel(p.x, CANVAS_WIDTH),
          toPixel(p.y, CANVAS_HEIGHT),
        ]);

        return (
          <Group
            key={item.id}
            x={px}
            y={py}
            onClick={handleClick}
            onTap={handleClick}
            onMouseEnter={(e) => {
              const container = e.target.getStage()?.container();
              if (container) container.style.cursor = 'pointer';
              
              const shape = e.target;
              const box = shape.getClientRect();

              setTooltip({
                 visible: true,
                 x: box.x + box.width / 2,
                 y: box.y,
                 bottomY: box.y + box.height,
                 item,
              });
            }}
            onMouseLeave={(e) => {
               const container = e.target.getStage()?.container();
               if (container) container.style.cursor = 'default';
               setTooltip(null);
            }}
          >
            <Path
              data={getRoundedPolygonPath(flatPoints, POLYGON_CORNER_RADIUS)}
              fill={fill}
              stroke={stroke}
              strokeWidth={strokeWidth}
            />
          </Group>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="relative">
      <Stage
        ref={stageRef}
        width={containerWidth}
        height={displayHeight}
        scaleX={scale}
        scaleY={scale}
        style={{ cursor: 'default' }}
      >
        <Layer>
          {/* Background image */}
          {backgroundImage && imageFit && (
            <KonvaImage
              image={backgroundImage}
              x={imageFit.offsetX}
              y={imageFit.offsetY}
              width={imageFit.drawWidth}
              height={imageFit.drawHeight}
            />
          )}

          {/* Table shapes */}
          {floorPlan.items.map((item) => renderTableItem(item))}
        </Layer>
      </Stage>
      {tooltip && tooltip.visible && (() => {
        // Flip tooltip below the shape if it's near the top (less than 60px from top)
        const flipped = tooltip.y < 60;
        return (
          <div
            className="absolute z-50 pointer-events-none"
            style={{
              top: flipped ? tooltip.bottomY + 8 : tooltip.y - 8,
              left: tooltip.x,
              transform: flipped ? 'translateX(-50%)' : 'translate(-50%, -100%)',
            }}
          >
            {/* Arrow on top when flipped */}
            {flipped && (
              <div className="flex justify-center mb-[-1px]">
                <div
                  style={{
                    width: 0, height: 0,
                    borderLeft: '6px solid transparent',
                    borderRight: '6px solid transparent',
                    borderBottom: '6px solid black',
                  }}
                />
              </div>
            )}

            <div className="rounded-xl border border-white/10 shadow-2xl px-3.5 py-2.5 min-w-[140px] bg-black">
              <p className="text-white font-semibold text-[13px] leading-tight truncate max-w-[180px] mb-1 text-center">
                {tooltip.item.name || `${tooltip.item.item_type && tooltip.item.item_type !== 'UNKNOWN' ? tooltip.item.item_type.replace('_', ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase()) : 'Item'} ${tooltip.item.table_number}`}
              </p>
              
              {soldOutCartTables.includes(tooltip.item.table_number) ? (
                <div className="mt-2 pt-1.5 border-t border-white/10">
                  <span className="text-red-500 font-medium text-[12px] uppercase tracking-wider block text-center">
                    In Cart · Sold Out
                  </span>
                </div>
              ) : !availableTables.includes(tooltip.item.table_number) ? (
                <div className="mt-2 pt-1.5 border-t border-white/10">
                  <span className="text-red-500 font-medium text-[12px] uppercase tracking-wider block text-center">
                    Sold Out
                  </span>
                </div>
              ) : (
                <div className="flex flex-col gap-1.5 mt-2 pt-1.5 border-t border-white/10">
                  <div className="flex items-center justify-between gap-3 text-[12px]">
                    <span className="text-gray-400">Price</span>
                    <span className="text-white font-medium">
                      {formatCurrencyForDisplay((pricePerGuest || 0) * (tooltip.item.table_size || 1), currency || 'USD')}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between gap-3 text-[12px]">
                    <span className="text-gray-400">Seats</span>
                    <span className="text-white font-medium">{tooltip.item.table_size}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Arrow on bottom when normal (above shape) */}
            {!flipped && (
              <div className="flex justify-center mt-[-1px]">
                <div
                  style={{
                    width: 0, height: 0,
                    borderLeft: '6px solid transparent',
                    borderRight: '6px solid transparent',
                    borderTop: '6px solid black',
                  }}
                />
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
};

export default FloorPlanViewer;
